import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarPlus,
  Loader2,
  Sparkles,
  Calendar as CalendarIcon,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DURATION_OPTIONS = [30, 45, 60, 90];
const WEEKDAY_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function isoToLocalParts(iso) {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

function formatSlotLabel(iso) {
  try {
    const d = new Date(iso);
    const wd = WEEKDAY_PT[d.getDay()];
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${wd} ${dd}/${mm} · ${hh}:${mi}`;
  } catch {
    return iso;
  }
}

/**
 * ScheduleInterviewDialog
 *
 * Form-only dialog. On successful submit, fires `onScheduled(interview, room)`
 * with the created interview and (optional) provisioned 100ms room. The host
 * is then responsible for closing this dialog and showing the success
 * payload in a *separate* dialog — that way our state isn't affected by the
 * parent re-rendering when it reloads its interview list.
 *
 * Props:
 *   open: boolean
 *   onClose(): void
 *   jobId: string (required)
 *   candidate: { id, name, phone, ... } (optional — if absent, user picks)
 *   candidatesPool: optional array to skip the fetch
 *   defaultManagerId: hint for the manager dropdown
 *   onScheduled(interview, room | null): callback after success
 */
const ScheduleInterviewDialog = ({
  open,
  onClose,
  jobId,
  candidate = null,
  candidatesPool = null,
  defaultManagerId = "",
  onScheduled,
}) => {
  const [managers, setManagers] = useState([]);
  const [candidates, setCandidates] = useState(candidatesPool || []);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const [candidateId, setCandidateId] = useState(candidate?.id || "");
  const [managerId, setManagerId] = useState(defaultManagerId || "");
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState("");

  const [suggestedSlots, setSuggestedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlotIso, setSelectedSlotIso] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const [customTime, setCustomTime] = useState("");

  const [submitting, setSubmitting] = useState(false);

  // Reset only on the false→true open transition. Avoids the form being
  // wiped if the dialog re-renders due to parent state changes.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setCandidateId(candidate?.id || "");
      setManagerId(defaultManagerId || "");
      setDuration(60);
      setNotes("");
      setSuggestedSlots([]);
      setSelectedSlotIso("");
      setCustomMode(false);
      setCustomDate("");
      setCustomTime("");
    }
    wasOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const finalStartsAtIso = useMemo(() => {
    if (customMode) {
      if (!customDate || !customTime) return "";
      const local = new Date(`${customDate}T${customTime}:00`);
      if (Number.isNaN(local.getTime())) return "";
      return local.toISOString();
    }
    return selectedSlotIso;
  }, [customMode, customDate, customTime, selectedSlotIso]);

  useEffect(() => {
    if (!open) return;
    setLoadingManagers(true);
    axios
      .get(`${API}/interviewers`)
      .then((res) => setManagers(res.data || []))
      .catch(() => setManagers([]))
      .finally(() => setLoadingManagers(false));
  }, [open]);

  useEffect(() => {
    if (!open || candidate || candidatesPool) return;
    setLoadingCandidates(true);
    axios
      .get(`${API}/jobs/${jobId}/candidates`)
      .then((res) => {
        const eligible = (res.data || []).filter(
          (c) => c.status !== "pendente" && c.status !== "erro",
        );
        setCandidates(eligible);
      })
      .catch(() => setCandidates([]))
      .finally(() => setLoadingCandidates(false));
  }, [open, jobId, candidate, candidatesPool]);

  useEffect(() => {
    if (!managerId) {
      setSuggestedSlots([]);
      setSelectedSlotIso("");
      return;
    }
    setLoadingSlots(true);
    setSelectedSlotIso("");
    axios
      .get(`${API}/availability/${managerId}/slots`, { params: { limit: 5 } })
      .then((res) => setSuggestedSlots(res.data || []))
      .catch(() => setSuggestedSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [managerId]);

  const canSubmit =
    !!candidateId && !!managerId && !!finalStartsAtIso && !submitting;

  const handleSchedule = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    let interview = null;
    let room = null;
    try {
      const interviewRes = await axios.post(`${API}/interviews`, {
        candidate_id: candidateId,
        starts_at: finalStartsAtIso,
        duration_minutes: duration,
        booked_via: "manual",
        notes: notes.trim(),
      });
      interview = interviewRes.data;

      try {
        const roomRes = await axios.post(
          `${API}/video/interviews/${interview.id}/room`,
          {},
        );
        room = roomRes.data;
      } catch (videoErr) {
        const detail =
          videoErr?.response?.data?.detail ||
          "Entrevista criada, mas não conseguimos preparar a sala de vídeo agora.";
        toast.warning(detail);
      }

      toast.success("Entrevista agendada com sucesso!");
      onScheduled?.(interview, room);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Não consegui agendar a entrevista. Tente outro horário.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    candidateId,
    finalStartsAtIso,
    duration,
    notes,
    onScheduled,
  ]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="schedule-interview-dialog"
        className="max-w-[560px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-brand-blue" />
            Agendar entrevista
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {candidate ? (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                Candidato
              </span>
              <div className="font-semibold text-slate-900">{candidate.name}</div>
              {candidate.email && (
                <div className="text-xs text-slate-500">{candidate.email}</div>
              )}
            </div>
          ) : (
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-bold block mb-1.5">
                Candidato *
              </label>
              <Select value={candidateId} onValueChange={setCandidateId}>
                <SelectTrigger data-testid="candidate-select">
                  <SelectValue
                    placeholder={
                      loadingCandidates
                        ? "Carregando candidatos..."
                        : "Selecione um candidato"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {candidates.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-500">
                      Nenhum candidato analisado disponível.
                    </div>
                  ) : (
                    candidates.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name || "(sem nome)"}
                        {c.score ? ` · ${c.score}%` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold block mb-1.5">
              Gestor entrevistador *
            </label>
            <Select value={managerId} onValueChange={setManagerId}>
              <SelectTrigger data-testid="manager-select">
                <SelectValue
                  placeholder={
                    loadingManagers ? "Carregando gestores..." : "Selecione o gestor"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {managers.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    Nenhum gestor disponível.
                  </div>
                ) : (
                  managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                      {m.has_availability ? " · 📅 agenda OK" : ""}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {managerId && (
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-bold block mb-1.5">
                Horário *
              </label>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-3">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Buscando próximos horários disponíveis…
                </div>
              ) : (
                <>
                  {!customMode && suggestedSlots.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-[11px] text-slate-500 flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-violet-500" />
                        Próximos horários disponíveis na agenda do gestor:
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {suggestedSlots.map((s) => (
                          <button
                            key={s.starts_at}
                            type="button"
                            onClick={() => setSelectedSlotIso(s.starts_at)}
                            data-testid={`slot-${s.starts_at}`}
                            className={`text-xs font-medium px-2.5 py-2 rounded-md border transition-colors ${
                              selectedSlotIso === s.starts_at
                                ? "bg-brand-blue text-white border-brand-blue"
                                : "bg-white border-slate-200 hover:border-brand-blue text-slate-700"
                            }`}
                          >
                            {formatSlotLabel(s.starts_at)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!customMode && suggestedSlots.length === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-800">
                      Este gestor ainda não cadastrou agenda em{" "}
                      <strong>Minha agenda</strong>. Use a opção{" "}
                      <strong>Outro horário</strong> abaixo.
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setCustomMode(!customMode);
                      if (customMode) {
                        setCustomDate("");
                        setCustomTime("");
                      } else {
                        setSelectedSlotIso("");
                        const now = new Date();
                        now.setHours(now.getHours() + 24);
                        const parts = isoToLocalParts(now.toISOString());
                        setCustomDate(parts.date);
                        setCustomTime("10:00");
                      }
                    }}
                    data-testid="toggle-custom-time"
                    className="mt-2 text-xs text-brand-blue hover:underline inline-flex items-center gap-1"
                  >
                    <Clock className="w-3 h-3" />
                    {customMode ? "← Usar horários sugeridos" : "Outro horário"}
                  </button>

                  {customMode && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                          Data
                        </label>
                        <input
                          type="date"
                          value={customDate}
                          onChange={(e) => setCustomDate(e.target.value)}
                          data-testid="custom-date"
                          className="w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                          Horário
                        </label>
                        <input
                          type="time"
                          value={customTime}
                          onChange={(e) => setCustomTime(e.target.value)}
                          data-testid="custom-time"
                          className="w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold block mb-1.5">
              Duração
            </label>
            <div className="flex gap-1.5">
              {DURATION_OPTIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDuration(m)}
                  data-testid={`duration-${m}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
                    duration === m
                      ? "bg-brand-blue text-white border-brand-blue"
                      : "bg-white border-slate-200 hover:border-brand-blue text-slate-700"
                  }`}
                >
                  {m} min
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold block mb-1.5">
              Anotações (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              data-testid="interview-notes"
              placeholder="Ex: focar em experiência com Airflow, perguntar sobre liderança..."
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 resize-none"
            />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <p className="text-[11px] text-slate-500">
              ✨ A sala de vídeo será gerada automaticamente
            </p>
            <div className="flex gap-2">
              <Button onClick={onClose} variant="outline" size="sm">
                Cancelar
              </Button>
              <Button
                onClick={handleSchedule}
                disabled={!canSubmit}
                data-testid="confirm-schedule"
                size="sm"
                className="bg-brand-blue hover:bg-brand-blue-dark text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Agendando…
                  </>
                ) : (
                  <>
                    <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
                    Agendar entrevista
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleInterviewDialog;
